import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '../../../components/ConfirmationModal';
import EmptyState from '../../../components/EmptyState';
import Modal from '../../../components/Modal';
import OrderedList from '../../../components/OrderedList';
import keyHasPermission from '../../../helpers/keyHasPermission';
import useProfile from '../../../hooks/useProfile';
import { IdAndName } from '../../../models/General';

interface Props {
  items: IdAndName[];
  open: boolean;
  onClose: () => void;
  onItemEdit: (item: IdAndName) => void;
  onItemRemove: (id: string) => Promise<any>;
  onItemMoveUp: (id: string) => Promise<any>;
  onItemMoveDown: (id: string) => Promise<any>;
}

function CategoryEditModal({
  items,
  open,
  onClose,
  onItemEdit,
  onItemRemove,
  onItemMoveUp,
  onItemMoveDown,
}: Props) {
  const { t } = useTranslation('category');
  const { meProfile } = useProfile();

  const [loading, setLoading] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<string | null>(
    null,
  );

  // todo: her yerde kullanılıyor, generic yapılabilir
  const handleRemove = async () => {
    if (deleteConfirmation) {
      setLoading(true);
      await onItemRemove(deleteConfirmation);
      setLoading(false);
      setDeleteConfirmation(null);
    }
  };

  const handleMoveUp = async (item: IdAndName) => {
    setLoading(true);
    await onItemMoveUp(item.id);
    setLoading(false);
  };

  const handleMoveDown = async (item: IdAndName) => {
    setLoading(true);
    await onItemMoveDown(item.id);
    setLoading(false);
  };

  return (
    <>
      <Modal
        title={t('category.editModal.title')}
        open={open}
        onClose={onClose}
        positiveAction={t('category.editModal.close')}
        onPositiveClick={onClose}
      >
        {items.length ? (
          <OrderedList
            items={items}
            loading={loading}
            onItemEdit={item => onItemEdit(item)}
            onItemRemove={item => setDeleteConfirmation(item.id)}
            onItemUp={handleMoveUp}
            onItemDown={handleMoveDown}
            editButtons={keyHasPermission(
              'update-category',
              meProfile?.permissions || [],
            )}
            removeButtons={keyHasPermission(
              'delete-category',
              meProfile?.permissions || [],
            )}
            sortButtons={keyHasPermission(
              'move-category-sequence',
              meProfile?.permissions || [],
            )}
          />
        ) : (
          <EmptyState message={t('category.editModal.empty')} />
        )}
      </Modal>
      <ConfirmationModal
        title={t('category.deleteConfirmation.title')}
        titleIconName="QuestionMarkOutlined"
        description={t('category.deleteConfirmation.description')}
        open={!!deleteConfirmation}
        onClose={() => setDeleteConfirmation(null)}
        positiveAction={t('category.deleteConfirmation.confirm')}
        onPositiveClick={handleRemove}
        negativeAction={t('category.deleteConfirmation.cancel')}
        onNegativeClick={() => setDeleteConfirmation(null)}
        loading={loading}
      />
    </>
  );
}

export default CategoryEditModal;
